import { lenis } from '@/app/components/common/SmoothScroll/SmoothScroll';

export function getApiURL() {
  return (
    process.env.STRAPI_API_URL ||
    process.env.NEXT_PUBLIC_API_URL ||
    'https://api.horenko-production.com'
  );
}

export function getStrapiURL() {
  return (
    process.env.NEXT_PUBLIC_STRAPI_URL ||
    process.env.STRAPI_API_URL ||
    'https://api.horenko-production.com'
  );
}

export function getSiteUrl() {
  return process.env.NEXT_PUBLIC_SITE_URL ?? 'http://localhost:3000';
}

export function getPageUrl(path) {
  return `${getSiteUrl()}${path}`;
}

export function getMediaURL(path) {
  return `${getStrapiURL()}${path}`;
}

export function getVideoData(video) {
  const { videoFile, isAutoplay, preview } = video;
  if (!videoFile) {
    return {
      src: '',
      autoplay: false,
      mime: 'video/mp4',
      poster: '',
    };
  }

  return {
    src: getMediaURL(videoFile.url),
    autoplay: isAutoplay,
    mime: videoFile.mime || 'video/mp4',
    poster: preview ? getMediaURL(preview.url) : '',
  };
}

/**
 * thumbnail, small, large, full
 */

export function getImageData(image, size = 'full') {
  if (!image) return null;

  const { formats, url, alternativeText } = image;

  let src = getMediaURL(url);

  if (size !== 'full' && formats && formats[size]) {
    src = getMediaURL(formats[size].url);
  }

  return {
    src,
    alt: alternativeText,
  };
}

export function extractYouTubeID(urlOrID) {
  // Regular expression for YouTube ID format
  const regExpID = /^[a-zA-Z0-9_-]{11}$/;

  // Check if the input is a YouTube ID
  if (regExpID.test(urlOrID)) {
    return urlOrID;
  }

  // Regular expression for standard YouTube links
  const regExpStandard = /youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/;

  // Regular expression for YouTube Shorts links
  const regExpShorts = /youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/;

  // Check for standard YouTube link
  const matchStandard = urlOrID.match(regExpStandard);
  if (matchStandard) {
    return matchStandard[1];
  }

  // Check for YouTube Shorts link
  const matchShorts = urlOrID.match(regExpShorts);
  if (matchShorts) {
    return matchShorts[1];
  }

  // Return null if no match is found
  return null;
}

export function toggleSiteScroll(shouldBlockScroll) {
  if (shouldBlockScroll) {
    document.documentElement.classList.add('no-scroll');
    document.body.classList.add('no-scroll');
    lenis && lenis.stop();
  } else {
    document.documentElement.classList.remove('no-scroll');
    document.body.classList.remove('no-scroll');
    lenis && lenis.start();
  }
}

export function formatTwoDigits(number) {
  return number.toString().padStart(2, '0');
}

export const getInfoCardPost = (post) => {
  if (post.page) {
    return {
      slug: 'page',
      urlPart: '',
      entity: post.page,
    };
  }

  if (post.niche) {
    return {
      slug: 'niche',
      urlPart: 'niches',
      entity: post.niche,
    };
  }

  if (post.technology) {
    return {
      slug: 'technology',
      urlPart: 'technologies',
      entity: post.technology,
    };
  }

  if (post.service) {
    return {
      slug: 'service',
      urlPart: 'services',
      entity: post.service,
    };
  }

  if (post.article) {
    return {
      slug: 'article',
      urlPart: 'articles',
      entity: post.article,
    };
  }

  if (post.subservice) {
    return {
      slug: 'subservice',
      urlPart: 'subservices',
      entity: post.subservice,
    };
  }

  return null;
};

export const preparePost = (post, locale) => {
  const postInfo = getInfoCardPost(post);

  if (!postInfo) {
    return {
      title: post.title || '',
      text: post.text || '',
      url: '',
      postTypeSlug: null,
    };
  }

  const { slug: postTypeSlug, urlPart, entity } = postInfo;

  const url = constructAbsoluteUrl(
    urlPart,
    entity?.slug,
    locale,
    entity.parentService?.slug || ''
  );

  const title = post.title || (entity && entity.name) || '';
  const text = post.text || (entity && entity.excerpt) || '';

  return {
    title,
    text,
    url,
    postTypeSlug,
  };
};

export function constructAbsoluteUrl(
  collectionKey,
  slug,
  locale,
  parentSlug = ''
) {
  const baseURL = process.env.NEXT_PUBLIC_SITE_URL;

  if (!baseURL) {
    console.warn(
      `NEXT_PUBLIC_SITE_URL environment variable missing. Cannot construct absolute URL for ${collectionKey}/${slug}.`
    );
    return null;
  }
  if (!slug) {
    console.warn(`Slug is missing for ${collectionKey}. Cannot construct URL.`);
    return null;
  }

  const defaultLocale = 'uk';
  let path = '';
  let finalUrl = '';

  if (collectionKey === 'subservices') {
    if (!parentSlug) {
      console.warn(
        `Subservice '${slug}' missing parentService slug. Cannot construct URL.`
      );
      return null;
    }
    path = `/services/${parentSlug}/${slug}`;
  } else if (collectionKey === 'articles') {
    path = `/blog/${slug}`;
  } else if (collectionKey === 'pages') {
    if (slug === 'home') {
      finalUrl =
        locale === defaultLocale ? `${baseURL}/` : `${baseURL}/${locale}/`;
      return finalUrl;
    }
    path = `/${slug}`;
  } else {
    path = `/${collectionKey}/${slug}`;
  }
  const localizedPath = locale === defaultLocale ? path : `/${locale}${path}`;
  finalUrl = `${baseURL}${localizedPath}`;

  finalUrl = finalUrl.replace(/([^:]\/)\/+/g, '$1');

  if (!finalUrl.endsWith('/')) {
    finalUrl += '/';
  }

  return finalUrl;
}

export function formatPhone(number) {
  if (!number) return '';
  return 'tel:' + number.replace(/\D/g, '');
}

export function isDeviceShit() {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent.toLowerCase();

  const isMobile = /iphone|android|ipad|mobile/.test(ua);
  const isOldMac =
    /macintosh/.test(ua) &&
    window.devicePixelRatio >= 2 &&
    navigator.hardwareConcurrency <= 4;
  const hasLowCores =
    navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
  const hasLowMemory = navigator.deviceMemory && navigator.deviceMemory <= 4;
  const isUnderpowered = hasLowCores || hasLowMemory;

  return isMobile || isOldMac || isUnderpowered;
}
